import React from "react";
import { Button, makeStyles, Text, typographyStyles } from "@fluentui/react-components";
import { ArrowLeft20Regular, ArrowRight20Regular } from "@fluentui/react-icons";
import "./SelectedParagraphHeading.css";

import type { OutlineMode } from "src/types";

const useStyles = makeStyles({
  // nowrap keeps the block's min-content width at the full title, so tight space
  // wraps the count onto the next line rather than splitting the title itself.
  heading: { ...typographyStyles.body1Strong, whiteSpace: "nowrap" },
  subheading: { ...typographyStyles.caption1, whiteSpace: "nowrap" },
});

export interface SelectedParagraphHeadingProps {
  loading?: boolean;
  noSelectedCharacterStyle?: boolean;
  nextDisabled?: boolean;
  outlineMode?: OutlineMode;
  previousDisabled?: boolean;
  selectionBounds?: [number, number?];
  total?: number;
  isMultiSelect?: boolean;
  onPrevious?: () => Promise<void>;
  onNext?: () => Promise<void>;
}

export function SelectedParagraphHeading({
  loading,
  noSelectedCharacterStyle,
  nextDisabled,
  outlineMode,
  previousDisabled,
  selectionBounds = [0],
  total = 0,
  isMultiSelect = false,
  onPrevious,
  onNext,
}: SelectedParagraphHeadingProps) {
  const styles = useStyles();
  const previousRef = React.useRef<HTMLButtonElement>(null);
  const nextRef = React.useRef<HTMLButtonElement>(null);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const isProcessingRef = React.useRef(false);

  // Navigating selects a range in the document, which hands focus to Word and
  // leaves the pane unfocused — the next click on an arrow is spent bringing
  // focus back rather than moving, and arrow keys drive the document instead of
  // the review list. Pull focus back to the button that was just used.
  const handlePrevious = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      await onPrevious?.();
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
    previousRef.current?.focus();
  };

  const handleNext = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    try {
      await onNext?.();
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
    nextRef.current?.focus();
  };

  const selectedText = noSelectedCharacterStyle
    ? ""
    : isMultiSelect
      ? `${selectionBounds[0] + 1}\u2013${(selectionBounds[1] ?? 0) + 1} of ${total}`
      : `${selectionBounds[0] + 1} of ${total}`;
  const selectedItemType = outlineMode === "character" ? "Character Style" : "Paragraph";
  const navTarget = outlineMode === "character" ? "character style" : "paragraph";

  return (
    <div className="selected-paragraph-heading-container">
      <div className="selected-paragraph-heading">
        <Text className={styles.heading}>
          {noSelectedCharacterStyle
            ? "Selection"
            : `Selected ${selectedItemType}${isMultiSelect ? "s" : ""}`}
        </Text>
        {!loading && <Text className={styles.subheading}>{selectedText}</Text>}
      </div>
      <div className="selected-paragraph-heading-navigation">
        <Button
          appearance="subtle"
          aria-label={`Previous ${navTarget}`}
          disabled={loading || isProcessing || previousDisabled}
          icon={<ArrowLeft20Regular />}
          ref={previousRef}
          size="small"
          onClick={handlePrevious}
        />
        <Button
          appearance="subtle"
          aria-label={`Next ${navTarget}`}
          disabled={loading || isProcessing || nextDisabled}
          icon={<ArrowRight20Regular />}
          ref={nextRef}
          size="small"
          onClick={handleNext}
        />
      </div>
    </div>
  );
}
